import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { AlertTriangle, CheckCircle2, PauseCircle, Plus, CalendarClock } from "lucide-react";

const VISIT_TYPES = ["Inspection", "Pool service", "Cleaning", "Garden", "Repair", "Owner arrival", "Meeting", "Delivery", "Meter reading", "Other"];

const fmtVisit = (iso) => {
  const d = new Date(iso);
  return isNaN(d.getTime()) ? iso : d.toLocaleString("en-GB", { weekday: "short", day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" });
};

function AddVisitForm({ propertyId, staff, onDone }) {
  const today = new Date().toISOString().slice(0, 10);
  const [f, setF] = useState({ date: today, time: "10:00", type: "Inspection", title: "", staff_id: staff[0]?.user_id || "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); e.stopPropagation();
    if (!f.staff_id) return toast.error("No staff member available to assign");
    const startD = new Date(`${f.date}T${f.time}`);
    if (isNaN(startD.getTime())) return toast.error("Pick a valid date and time");
    setBusy(true);
    try {
      await api.post("/appointments?force=true", {
        property_id: propertyId, staff_id: f.staff_id, type: f.type,
        title: f.title.trim() || f.type,
        start: startD.toISOString(), end: new Date(startD.getTime() + 60 * 60 * 1000).toISOString(),
      });
      toast.success("Visit booked");
      onDone(true);
    } catch (err) { toast.error(apiErr(err)); } finally { setBusy(false); }
  };
  return (
    <form onClick={(e) => e.stopPropagation()} onSubmit={submit} style={{ marginTop: 12, padding: 14, background: "var(--soft)", borderRadius: 10, display: "grid", gap: 10 }} data-testid="add-visit-form">
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <input className="inp" type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} style={{ maxWidth: 160 }} data-testid="visit-date-input" />
        <input className="inp" type="time" value={f.time} onChange={(e) => setF({ ...f, time: e.target.value })} style={{ maxWidth: 120 }} data-testid="visit-time-input" />
        <select className="inp" value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })} style={{ maxWidth: 170 }} data-testid="visit-type-select">{VISIT_TYPES.map((t) => <option key={t}>{t}</option>)}</select>
      </div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <input className="inp" placeholder="Note (optional)" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} style={{ flex: 1, minWidth: 160 }} data-testid="visit-title-input" />
        <select className="inp" value={f.staff_id} onChange={(e) => setF({ ...f, staff_id: e.target.value })} style={{ maxWidth: 180 }} data-testid="visit-staff-select">{staff.map((s) => <option key={s.user_id} value={s.user_id}>{s.name}</option>)}</select>
      </div>
      <div style={{ display: "flex", gap: 10 }}>
        <button className="btn-ink" type="submit" disabled={busy} style={{ padding: "6px 14px" }} data-testid="visit-save-btn">{busy ? "Booking…" : "Book visit"}</button>
        <button className="btn-outline" type="button" onClick={() => onDone(false)} style={{ padding: "6px 14px" }}>Cancel</button>
      </div>
    </form>
  );
}

function Row({ r, reload, onOpenProperty, selected, onToggleSelect, staff }) {
  const [visits, setVisits] = useState(r.paid ?? 0);
  const [adding, setAdding] = useState(false);
  const paused = !!r.paused;
  const booked = r.visits || [];
  const stop = (e) => e.stopPropagation();
  const saveVisits = async (e) => {
    stop(e);
    try { await api.patch(`/properties/${r.property_id}/plan`, { visits_per_month: parseInt(visits) || 0 }); toast.success("Visit count updated"); reload(); }
    catch (err) { toast.error(apiErr(err)); }
  };
  const togglePause = async (e) => {
    stop(e);
    try { await api.patch(`/properties/${r.property_id}/plan`, { paused: !paused }); toast.success(!paused ? "Plan paused" : "Plan resumed"); reload(); }
    catch (err) { toast.error(apiErr(err)); }
  };
  const border = paused ? "#8c8577" : r.warnings.length ? "#c68a3a" : "#5B6440";
  return (
    <div className="card-soft" style={{ padding: 18, cursor: "pointer", borderLeft: `4px solid ${border}` }} onClick={() => onOpenProperty(r.property_id)} data-testid="schedule-row">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10, flexWrap: "wrap" }}>
        <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
          <input type="checkbox" checked={selected} onClick={stop} onChange={() => onToggleSelect(r.property_id)} style={{ marginTop: 5, width: 16, height: 16, cursor: "pointer" }} data-testid="schedule-select-checkbox" />
          <div>
            <span className="lbl-m" style={{ color: "var(--honey)" }}>{r.ref}</span>
            <div style={{ fontSize: 18, fontWeight: 500 }}>{r.name}</div>
            <div className="lbl-m">{r.tier || "-"} · {r.paid} paid visit(s)/mo</div>
          </div>
        </div>
        <div style={{ textAlign: "right", fontSize: 13, color: "var(--muted)" }}>{r.done} done · {r.scheduled} scheduled · {r.this_week} this week</div>
      </div>
      <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 12, flexWrap: "wrap" }} onClick={stop}>
        <span className="lbl-m">Paid visits / month</span>
        <input className="inp" type="number" style={{ width: 74 }} value={visits} onChange={(e) => setVisits(e.target.value)} data-testid="schedule-visits-input" />
        <button className="btn-outline" style={{ padding: "5px 11px" }} onClick={saveVisits} data-testid="schedule-save-visits-btn">Save</button>
        <button className="btn-outline" style={{ padding: "5px 11px" }} onClick={togglePause} data-testid="schedule-toggle-pause-btn">{paused ? "Resume" : "Pause"}</button>
        <button className="btn-ink" style={{ padding: "5px 11px", display: "inline-flex", alignItems: "center", gap: 5 }} onClick={(e) => { stop(e); setAdding((v) => !v); }} data-testid="schedule-add-visit-btn"><Plus size={14} />Book a visit</button>
      </div>
      {adding && <AddVisitForm propertyId={r.property_id} staff={staff} onDone={(created) => { setAdding(false); if (created) reload(); }} />}
      <div style={{ marginTop: 12, borderTop: "1px solid var(--line)", paddingTop: 10 }} onClick={stop}>
        <div className="lbl-m" style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}><CalendarClock size={13} />Booked this month · {booked.length}</div>
        {booked.length === 0
          ? <div style={{ fontSize: 13, color: "var(--muted)" }}>No visits booked this month yet.</div>
          : <div style={{ display: "grid", gap: 6 }} data-testid="schedule-visits-list">
              {booked.map((v) => (
                <div key={v.appointment_id} style={{ display: "flex", justifyContent: "space-between", gap: 10, fontSize: 13, padding: "4px 0" }} data-testid="schedule-visit-item">
                  <span><b style={{ fontWeight: 500 }}>{fmtVisit(v.start)}</b> · {v.title || v.type}</span>
                  <span className="pill-tag" style={{ background: v.status === "completed" ? "#e7ecdf" : "var(--sand)", color: v.status === "completed" ? "#5B6440" : "var(--umber)" }}>{v.status === "completed" ? "Done" : "Scheduled"}</span>
                </div>
              ))}
            </div>}
      </div>
      {paused
        ? <div style={{ marginTop: 10 }}><span className="pill-tag" style={{ background: "var(--sand)", color: "var(--umber)", display: "inline-flex", gap: 5, alignItems: "center" }} data-testid="schedule-paused-badge"><PauseCircle size={12} />Plan paused</span></div>
        : r.warnings.length
          ? <div style={{ marginTop: 10, display: "flex", gap: 8, flexWrap: "wrap" }}>{r.warnings.map((w, i) => <span key={i} className="pill-tag" style={{ background: "#f3e4cf", color: "#8a5a1f", display: "inline-flex", gap: 5, alignItems: "center" }}><AlertTriangle size={12} />{w}</span>)}</div>
          : <div style={{ marginTop: 10 }}><span className="pill-tag" style={{ background: r.paid ? "#e7ecdf" : "var(--sand)", color: r.paid ? "#5B6440" : "var(--umber)", display: "inline-flex", gap: 5, alignItems: "center" }}>{r.paid ? <><CheckCircle2 size={12} />On track</> : "No plan yet"}</span></div>}
    </div>
  );
}

export default function StaffSchedule({ onOpenProperty }) {
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState([]);
  const [staff, setStaff] = useState([]);
  const load = () => api.get("/schedule/overview").then(({ data }) => setRows(data)).catch((e) => toast.error(apiErr(e)));
  useEffect(() => { load(); api.get("/staff-members").then(({ data }) => setStaff(data)).catch(() => {}); }, []);
  const flagged = rows.filter((r) => !r.paused && r.warnings.length);
  const toggleSelect = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const bulkSetPaused = async (paused) => {
    if (!selected.length) return;
    try {
      await Promise.all(selected.map((id) => api.patch(`/properties/${id}/plan`, { paused })));
      toast.success(`${selected.length} plan(s) ${paused ? "paused" : "resumed"}`);
      setSelected([]); load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <div data-testid="staff-schedule">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <p style={{ color: "var(--muted)", marginTop: 0 }}>What needs booking across your properties this month. {flagged.length ? `${flagged.length} need attention.` : "All on track."}</p>
      </div>
      {selected.length > 0 && (
        <div className="card-soft" style={{ padding: "12px 16px", marginBottom: 12, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }} data-testid="bulk-action-bar">
          <span style={{ fontSize: 14 }}>{selected.length} selected</span>
          <button className="btn-ink" style={{ padding: "6px 14px" }} onClick={() => bulkSetPaused(true)} data-testid="bulk-pause-btn">Pause selected</button>
          <button className="btn-outline" style={{ padding: "6px 14px" }} onClick={() => bulkSetPaused(false)} data-testid="bulk-resume-btn">Resume selected</button>
          <button className="btn-outline" style={{ padding: "6px 14px" }} onClick={() => setSelected([])} data-testid="bulk-clear-btn">Clear</button>
        </div>
      )}
      <div style={{ display: "grid", gap: 12 }}>
        {rows.map((r) => <Row key={r.property_id} r={r} reload={load} onOpenProperty={onOpenProperty} selected={selected.includes(r.property_id)} onToggleSelect={toggleSelect} staff={staff} />)}
        {rows.length === 0 && <p style={{ color: "var(--muted)" }}>No properties yet.</p>}
      </div>
    </div>
  );
}
